/**
 * My Journey Page
 * 
 * A dedicated page for members to track their progress, wins, badges,
 * and accountability circle engagement. Moved from dashboard to reduce clutter.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import ProgressWinsWidget from '@/components/ProgressWinsWidget';
import CrownJewelsWidget from '@/components/CrownJewelsWidget';
// AccountabilityCircleWidget moved to Community Hub
import { QuietRecognitionCard, ReflectionPrompt } from '@/components/QuietRecognition';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Trophy, TrendingUp, Star, Heart, Users, Sparkles, 
  ArrowLeft, Mail, Loader2, ChevronRight, Crown
} from 'lucide-react';

const API_URL = window.location.origin;

const MyJourneyPage = () => {
  const { user, hasInnerCircleAccess } = useAuth();
  const [focusPath, setFocusPath] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestSent, setDigestSent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newJewelsAlert, setNewJewelsAlert] = useState(0);

  useEffect(() => {
    fetchFocusPath();
  }, []);

  const fetchFocusPath = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/user/focus-path`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFocusPath(response.data);
    } catch (error) {
      console.log('No focus path set yet');
    } finally {
      setLoading(false);
    }
  };

  const handleNewJewels = (count) => {
    setNewJewelsAlert(count);
  };

  const fetchWeeklyDigest = async () => {
    setDigestLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/journal/weekly-digest/send-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDigestSent(true);
      toast.success('Your weekly digest has been sent to your email!');
    } catch (error) {
      toast.error('Failed to generate digest. Please try again.');
    } finally {
      setDigestLoading(false);
    }
  };

  if (!hasInnerCircleAccess || !hasInnerCircleAccess()) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card className="card-base text-center py-12">
            <CardContent>
              <Trophy className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-semibold text-forest mb-2">Unlock Your Journey</h2>
              <p className="text-gray-600 mb-4">Join the Inner Circle to track your progress and celebrate your wins.</p>
              <Link to="/inner-circle">
                <Button className="btn-primary">Learn More</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-500 hover:text-forest mb-4">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-terracotta to-purple-600 flex items-center justify-center">
              <Trophy className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-forest">My Journey</h1>
              <p className="text-gray-600">Track your progress, celebrate your wins, and connect with your circle.</p>
            </div>
          </div>
        </div>

        {/* Journey Stats Overview */}
        {focusPath && (
          <Card className="card-base mb-8 bg-gradient-to-r from-terracotta/5 via-purple-50/30 to-forest/5">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Your Sacred Focus</p>
                  <h2 className="text-xl font-heading font-semibold text-forest">{focusPath.focus_label}</h2>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-terracotta">{focusPath.day_number}</p>
                    <p className="text-xs text-gray-500">Days In</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-purple-600">{focusPath.progress_percent}%</p>
                    <p className="text-xs text-gray-500">Complete</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Progress & Wins */}
          <ProgressWinsWidget />
          
          {/* Crown Jewels & Accountability */}
          <div className="space-y-6">
            <div className="relative">
              {newJewelsAlert > 0 && (
                <div className="absolute -top-2 -right-2 z-10">
                  <Badge className="bg-red-500 text-white animate-pulse flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    {newJewelsAlert} New!
                  </Badge>
                </div>
              )}
              <CrownJewelsWidget onNewJewels={handleNewJewels} />
            </div>
            {/* AccountabilityCircleWidget moved to Community Hub */}
          </div>
        </div>

        {/* Weekly Journal Digest */}
        <Card className="card-base mb-8 overflow-hidden" data-testid="weekly-digest-card">
          <div className="bg-gradient-to-r from-forest/10 via-purple-50/50 to-terracotta/10 p-1">
            <CardContent className="bg-white rounded-lg p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-forest to-purple-600 flex items-center justify-center shrink-0">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-forest flex items-center gap-2">
                      Your Weekly Journal Digest
                      <Badge className="bg-forest/10 text-forest text-xs">AI-Powered</Badge>
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Get a personalized summary of your journaling journey this week, sent to your email.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={fetchWeeklyDigest}
                    disabled={digestLoading || digestSent}
                    className="shrink-0"
                  >
                    {digestLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                    ) : digestSent ? (
                      <><Mail className="h-4 w-4 mr-2" />Sent!</>
                    ) : (
                      <><Mail className="h-4 w-4 mr-2" />Send to Email</>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </div>
        </Card>

        {/* Quiet Recognition & Reflection */}
        {focusPath && (
          <div className="grid md:grid-cols-2 gap-6">
            <QuietRecognitionCard focusArea={focusPath?.focus_area || 'general'} />
            <ReflectionPrompt 
              timeOfDay={new Date().getHours() < 12 ? 'morning' : 'evening'}
              focusArea={focusPath?.focus_area || 'general'}
            />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MyJourneyPage;
